// @ts-check
import _set from 'lodash/set';
import _merge from 'lodash/merge';
import _isEmpty from 'lodash/isEmpty';

const UCA_ALPHA_SIZES = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '4XL',
  'A',
  'B',
  'C',
  'D',
  'DD',
  'G',
  '1X',
  '2X',
  '3X',
  '4X',
];
const EXTRA_OPTION_TEXT = 'Other';

/**
 * @typedef {Object} FreeFormData
 * @property {Array<string>} values
 * @property {boolean} [editable]
 * @property {boolean} [systemGenerated]
 */

/**
 * @typedef {Object} NewFreeFormData
 * @property {string} data
 * @property {boolean} [editable]
 * @property {boolean} [systemGenerated]
 */

/**
 * @param {FreeFormData} data
 * @returns {(string|NewFreeFormData)}
 */
const convertFreeFormData = ({ values = [], editable, systemGenerated }, isExtraOption = false) => {
  if (isExtraOption) {
    return values[0];
  }
  return {
    data: values[0],
    editable,
    systemGenerated,
  };
};

/**
 * @param {string | undefined} data
 * @returns {FreeFormData}
 */
const retrieveFreeFormValue = (data) => ({ values: data ? [data] : [] });

/**
 * @typedef {Object} Option
 * @property {string} text
 */

/**
 * @typedef {Object} SelectableData
 * @property {Array<string>} values
 * @property {Array<Option>} options
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @typedef {Object} ConvertedOption
 * @property {string} label
 * @property {boolean} selected
 */

/**
 * @typedef {Object} ExtraOption
 * @property {true} isExtraOption
 * @property {string|NewFreeFormData} [value]
 */

/**
 * @typedef {Object} NewConvertedOptions
 * @property {ConvertedOptions} data
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @typedef {Array<ConvertedOption | ExtraOption>} ConvertedOptions
 */

/**
 * @param {SelectableData} data
 * @param {boolean} [hasExtraOption] Indicates that there should be a free form option ('Other') for the attribute.
 * @param {FreeFormData} [extraOptionData] Contains the value of the free form option.
 * @returns {NewConvertedOptions}
 */
const convertSelectableData = (
  { options = [], values = [], editable, systemGenerated },
  hasExtraOption,
  extraOptionData = { values: [] },
) => {
  /** @type {ConvertedOptions}*/
  const selectableOptions = options.map(({ text, ...option }) => {
    if (hasExtraOption && text === EXTRA_OPTION_TEXT) {
      return {
        ...option,
        value: convertFreeFormData(extraOptionData, true),
        isExtraOption: true,
      };
    }

    return {
      ...option,
      label: text,
      selected: values.includes(text),
    };
  });

  return {
    data: selectableOptions,
    editable,
    systemGenerated,
  };
};

/**
 * @param {ConvertedOptions} options
 * @returns {Array<Pick<SelectableData, values>, Pick<SelectableData, values>>}
 */
const retrieveSelectableValues = (options) => {
  /** @type {Array<string>} */
  const values = [];
  /** @type {Array<string>} */
  const other = [];

  // @ts-ignore
  options.forEach(({ label, selected, value, isExtraOption }) => {
    if (selected) {
      values.push(label);
    } else if (isExtraOption && value) {
      values.push(EXTRA_OPTION_TEXT);
      other.push(value);
    }
  });

  return [{ values }, { values: other }];
};

/**
 * @typedef {Object} HeightData
 * @property {Array<string | undefined, string | undefined>} values
 */

/**
 * @typedef {Object} HeightDataV2
 * @property {Array<string | undefined, string | undefined>} values
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @typedef {Object} HeightValue
 * @property {string} [foots]
 * @property {string} [inches]
 */

/**
 * @typedef {Object} NewHeightValue
 * @property {HeightValue} data
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @param {HeightDataV2} data
 * @returns {NewHeightValue}
 */
const convertHeightData = ({ values = [], editable, systemGenerated }) => ({
  data: {
    foots: values[0],
    inches: values[1],
  },
  editable,
  systemGenerated,
});

/**
 * @param {HeightValue} data
 * @returns {HeightData}
 */
const retrieveHeightValue = (data) => ({
  values: data?.foots && data?.inches ? [data.foots, data.inches] : [],
});

/**
 * @typedef {Object} FitData
 * @property {HeightDataV2} height
 * @property {SelectableData} bodyType
 * @property {SelectableData} petiteOrPlus
 * @property {SelectableData} dressSize
 * @property {SelectableData} topSizeMen
 * @property {SelectableData} topSizeWomen
 * @property {SelectableData} pantsJeanSizeMen
 * @property {SelectableData} pantsJeanSizeWomen
 * @property {SelectableData} jeanWaistMen
 * @property {SelectableData} jeanWaistWomen
 * @property {SelectableData} jeanStyleMen
 * @property {SelectableData} jeanStyleWomen
 * @property {SelectableData} shoeSizeMen
 * @property {SelectableData} shoeSizeWomen
 * @property {SelectableData} fitChallenges
 * @property {SelectableData} dressFit
 * @property {SelectableData} pantsFitMen
 * @property {SelectableData} jeanRise
 * @property {SelectableData} skirtSize
 * @property {SelectableData} sleeveLength
 * @property {SelectableData} heelHeight
 * @property {SelectableData} jeanLength
 * @property {SelectableData} pantInseam
 * @property {SelectableData} braSize
 * @property {SelectableData} shirtSize
 * @property {SelectableData} shirtFit
 * @property {SelectableData} suitSize
 */

/**
 * @param {string} alphaSize
 * @returns {string}
 */
const convertUCAAlphaSize = (alphaSize) => {
  switch (alphaSize) {
    case '1X':
      return 'XL';
    case '2X':
      return 'XXL';
    case '3X':
      return '3XL';
    case '4X':
      return '4XL';
    default:
      return alphaSize;
  }
};

/**
 * @param {string} size
 * @param {string} stringToReplace
 * @returns {string}
 */
const convertUCASize = (size, stringToReplace) => {
  return size.replace(stringToReplace, '').trim();
};

/**
 * @typedef {Object} ConvertedSizeOption
 * @property {boolean} selected
 * @property {string} text
 * @property {string} label
 */

/**
 * @typedef {Object} ProportionsSizes
 * @property {ConvertedSizeOption[]} petite
 * @property {ConvertedSizeOption[]} plus
 * @property {ConvertedSizeOption[]} regular
 */

/**
 * @typedef {Object} ConvertedSizeOptions
 * @property {ProportionsSizes} alpha
 * @property {ProportionsSizes} us
 * @property {ProportionsSizes} eu
 */

/**
 * @typedef {Object} NewConvertedSizeOptions
 * @property {Object} data
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @param {SelectableData} data
 * @returns {NewConvertedSizeOptions}
 */
const convertSizeData = ({ options = [], values = [], editable, systemGenerated }) => {
  /** @type {ConvertedSizeOption[]} */
  const petite = [];
  /** @type {ConvertedSizeOption[]} */
  const plus = [];
  /** @type {ConvertedSizeOption[]} */
  const regular = [];
  const proportions = { petite, plus, regular };
  const alpha = {};
  const us = {};
  const eu = {};

  options.forEach(({ text }) => {
    const selected = values.includes(text);

    if (text.includes('Petite')) {
      const label = convertUCASize(text, 'Petite');

      petite.push({ label, selected, text });
      return;
    }

    if (text.includes('Plus')) {
      const label = convertUCASize(text, 'Plus');

      plus.push({ label, selected, text });
      return;
    }

    regular.push({ label: text, selected, text });
  });

  Object.keys(proportions).forEach((key) => {
    proportions[key].forEach((option) => {
      if (option.label.includes('EU')) {
        const convertedOption = {
          ...option,
          label: convertUCASize(option.label, 'EU'),
        };

        if (key in eu) {
          eu[key].push(convertedOption);
        } else {
          eu[key] = [convertedOption];
        }

        return;
      }

      if (UCA_ALPHA_SIZES.includes(option.label)) {
        const convertedOption = {
          ...option,
          label: convertUCAAlphaSize(option.label),
        };

        if (key in alpha) {
          alpha[key].push(convertedOption);
        } else {
          alpha[key] = [convertedOption];
        }
        return;
      }

      if (key in us) {
        us[key].push(option);
      } else {
        us[key] = [option];
      }
    });
  });

  // @ts-ignore
  const data = Object.entries({ alpha, us, eu }).reduce((result, [key, value]) => {
    if (!_isEmpty(value)) {
      return { ...result, [key]: value };
    }

    return result;
  }, {});

  return {
    data,
    editable,
    systemGenerated,
  };
};

/**
 * @param {ConvertedSizeOptions} sizeGroups
 * @returns {Pick<SelectableData, values>}
 */
const retrieveSizeValue = (sizeGroups) => {
  /** @type {Array<string>} */
  const values = [];

  Object.values(sizeGroups).forEach((sizeGroup) => {
    Object.values(sizeGroup).forEach((options) => {
      const selectedOptions = options.filter(({ selected }) => selected);
      const selectedValues = selectedOptions.map(({ text }) => text);

      values.push(...selectedValues);
    });
  });

  return { values };
};

/**
 * @typedef {Object} MultipleSelectData
 * @property {Array<string>} data
 * @property {boolean} editable
 * @property {boolean} systemGenerated
 */

/**
 * @param {SelectableData} values
 * @returns {MultipleSelectData}
 */
const convertMultipleSelectData = ({ values = [], editable, systemGenerated }) => ({
  data: values,
  editable,
  systemGenerated,
});

/**
 * @typedef {Object} ConvertedFitData
 * @property {NewHeightValue} height
 * @property {NewConvertedOptions} bodyType
 * @property {NewConvertedOptions} petiteOrPlus
 * @property {NewConvertedSizeOptions} dressSize
 * @property {NewConvertedSizeOptions} topSizeMen
 * @property {NewConvertedSizeOptions} topSizeWomen
 * @property {NewConvertedOptions} sleeveLength
 * @property {NewConvertedSizeOptions} pantsJeanSizeMen
 * @property {NewConvertedSizeOptions} pantsJeanSizeWomen
 * @property {NewConvertedSizeOptions} jeanWaistMen
 * @property {NewConvertedSizeOptions} jeanWaistWomen
 * @property {NewConvertedOptions} jeanStyleMen
 * @property {NewConvertedOptions} jeanStyleWomen
 * @property {NewConvertedSizeOptions} shoeSizeMen
 * @property {NewConvertedSizeOptions} shoeSizeWomen
 * @property {NewConvertedOptions} fitChallenges
 * @property {NewConvertedOptions} dressFit
 * @property {NewConvertedOptions} pantsFit
 * @property {NewConvertedOptions} jeanRise
 * @property {NewConvertedSizeOptions} skirtSize
 * @property {NewConvertedOptions} heelHeight
 * @property {NewConvertedOptions} jeanLength
 * @property {NewConvertedOptions} pantInseam
 * @property {NewConvertedSizeOptions} braSize
 * @property {NewConvertedSizeOptions} suitSize
 * @property {NewConvertedSizeOptions} shirtSize
 * @property {NewConvertedOptions} shirtFit
 */

/**
 * @param {FitData} data
 * @returns {ConvertedFitData}
 */
const convertFit = (data) => ({
  height: convertHeightData(data.height),
  bodyType: convertSelectableData(data.bodyType),
  petiteOrPlus: convertSelectableData(data.petiteOrPlus),
  dressSize: convertSizeData(data.dressSize),
  topSizeMen: convertSizeData(data.topSizeMen),
  topSizeWomen: convertSizeData(data.topSizeWomen),
  sleeveLength: convertSelectableData(data.sleeveLength),
  pantsJeanSizeMen: convertSizeData(data.pantsJeanSizeMen),
  pantsJeanSizeWomen: convertSizeData(data.pantsJeanSizeWomen),
  jeanWaistMen: convertSizeData(data.jeanWaistMen),
  jeanWaistWomen: convertSizeData(data.jeanWaistWomen),
  jeanStyleMen: convertSelectableData(data.jeanStyleMen),
  jeanStyleWomen: convertSelectableData(data.jeanStyleWomen),
  shoeSizeMen: convertSizeData(data.shoeSizeMen),
  shoeSizeWomen: convertSizeData(data.shoeSizeWomen),
  fitChallenges: convertSelectableData(data.fitChallenges),
  dressFit: convertSelectableData(data.dressFit),
  pantsFit: convertSelectableData(data.pantsFitMen),
  jeanRise: convertSelectableData(data.jeanRise),
  skirtSize: convertSizeData(data.skirtSize),
  heelHeight: convertSelectableData(data.heelHeight),
  jeanLength: convertSelectableData(data.jeanLength),
  pantInseam: convertSelectableData(data.pantInseam),
  braSize: convertSizeData(data.braSize),
  suitSize: convertSizeData(data.suitSize),
  shirtSize: convertSizeData(data.shirtSize),
  shirtFit: convertSelectableData(data.shirtFit),
});

/**
 * @typedef {Object} LifestyleData
 * @property {FreeFormData} profession
 * @property {SelectableData} interests
 * @property {FreeFormData} interestsOther
 * @property {FreeFormData} styleInfluencers
 */

/**
 * @typedef {Object} ConvertedLifestyleData
 * @property {string|NewFreeFormData} profession
 * @property {NewConvertedOptions} interests
 * @property {string|NewFreeFormData} styleInfluences
 */

/**
 * @param {LifestyleData} lifestyle
 * @returns {ConvertedLifestyleData}
 */
const convertLifestyle = ({ profession, interests, interestsOther, styleInfluencers }) => ({
  profession: convertFreeFormData(profession),
  interests: convertSelectableData(interests, true, interestsOther),
  styleInfluences: convertFreeFormData(styleInfluencers),
});

/**
 * @typedef {Object} StyleData
 * @property {SelectableData} brands
 * @property {SelectableData} stylingOccasions
 * @property {FreeFormData} stylingOccasionsOther
 * @property {SelectableData} wardrobeStyleMen
 * @property {SelectableData} wardrobeStyleWomen
 * @property {SelectableData} colors
 * @property {SelectableData} patterns
 * @property {SelectableData} dressOrPants
 * @property {SelectableData} jewelryStyle
 * @property {FreeFormData} jewelryStyleOther
 * @property {SelectableData} jewelryTone
 * @property {FreeFormData} jewelryToneOther
 * @property {SelectableData} earsPierced
 * @property {FreeFormData} fabrics
 */

/**
 * @typedef {Object} ConvertedStyleData
 * @property {MultipleSelectData} brands
 * @property {NewConvertedOptions} stylingOccasions
 * @property {{data:{ men: NewConvertedOptions, women: NewConvertedOptions}, editable, systemGenerated}} wardrobeStyle
 * @property {NewConvertedOptions} colors
 * @property {NewConvertedOptions} patterns
 * @property {NewConvertedOptions} dressOrPants
 * @property {NewConvertedOptions} jewelryStyle
 * @property {NewConvertedOptions} jewelryTone
 * @property {NewConvertedOptions} earsPierced
 * @property {string|NewFreeFormData} fabrics
 */

/**
 * @param {StyleData} data
 * @returns {ConvertedStyleData}
 */
const convertStyle = (data) => ({
  brands: convertMultipleSelectData(data.brands),
  stylingOccasions: convertSelectableData(data.stylingOccasions, true, data.stylingOccasionsOther),
  wardrobeStyle: {
    data: {
      men: convertSelectableData(data.wardrobeStyleMen),
      women: convertSelectableData(data.wardrobeStyleWomen),
    },
    editable: data.wardrobeStyleMen.editable,
    systemGenerated: data.wardrobeStyleMen.systemGenerated,
  },
  colors: convertSelectableData(data.colors),
  patterns: convertSelectableData(data.patterns),
  dressOrPants: convertSelectableData(data.dressOrPants),
  jewelryStyle: convertSelectableData(data.jewelryStyle, true, data.jewelryStyleOther),
  jewelryTone: convertSelectableData(data.jewelryTone, true, data.jewelryToneOther),
  earsPierced: convertSelectableData(data.earsPierced),
  fabrics: convertFreeFormData(data.fabrics),
});

/**
 * @typedef {Object} ShoppingHabitsData
 * @property {SelectableData} feelsAboutShopping
 * @property {SelectableData} shoppingPreference
 * @property {SelectableData} shopsFor
 */

/**
 * @typedef {Object} ConvertedShoppingHabitsData
 * @property {NewConvertedOptions} feelsAboutShopping
 * @property {NewConvertedOptions} shoppingPreference
 * @property {NewConvertedOptions} shopsFor
 */

/**
 * @param {ShoppingHabitsData} shoppingHabits
 * @returns {ConvertedShoppingHabitsData}
 */
const convertShoppingHabits = ({ feelsAboutShopping, shoppingPreference, shopsFor }) => ({
  feelsAboutShopping: convertSelectableData(feelsAboutShopping),
  shoppingPreference: convertSelectableData(shoppingPreference),
  shopsFor: convertSelectableData(shopsFor),
});

/**
 * @typedef ConvertedStylePreferences
 * @property {ConvertedStyleData} stylePreferences
 * @property {ConvertedLifestyleData} lifestyle
 * @property {ConvertedFitData} fit
 * @property {ConvertedShoppingHabitsData} shoppingHabits
 * @property {Array<string>} emptyProfiles Contains the UCA profile IDs that do not have a style preference record yet.
 */

/**
 * @param {{
 *   preferences: {
 *     emptyProfiles: Array<string>,
 *     values: {
 *       style: StyleData,
 *       lifestyle: LifestyleData,
 *       fit: FitData,
 *       shoppingHabits: ShoppingHabitsData,
 *     }
 *   },
 * }} data The profile data from API response.
 * @returns {{ style?: ConvertedStylePreferences }} Converted profile data with mapped style preferences.
 */
export const convertStylePreferences = ({ preferences, ...rest }) => {
  /** @type {{ style?: ConvertedStylePreferences }} */
  const profileData = { ...rest };

  if (preferences) {
    profileData.style = {
      stylePreferences: convertStyle(preferences.values.style),
      lifestyle: convertLifestyle(preferences.values.lifestyle),
      fit: convertFit(preferences.values.fit),
      shoppingHabits: convertShoppingHabits(preferences.values.shoppingHabits),
      emptyProfiles: preferences.emptyProfiles,
    };
  }

  return profileData;
};

/**
 * @param {string} section The style preference section name in store.
 * @returns {string} The section name according to API.
 */
const getSectionPath = (section) => {
  const sectionName = section === 'stylePreferences' ? 'style' : section;

  return `preferences.${sectionName}`;
};

/**
 * @param {*} data The state of specific style preference attribute from store.
 * @param {string} attribute The name of the style preference attribute.
 */
const getAttributeValue = (data, attribute) => {
  switch (attribute) {
    case 'height':
      return {
        [attribute]: retrieveHeightValue(data),
      };
    case 'brands':
      return {
        [attribute]: { values: data },
      };
    case 'fabrics':
    case 'profession':
      return {
        [attribute]: retrieveFreeFormValue(data),
      };
    case 'styleInfluences':
      return {
        styleInfluencers: retrieveFreeFormValue(data),
      };
    case 'wardrobeStyle':
      return {
        wardrobeStyleMen: retrieveSelectableValues(data.men.data)[0],
        wardrobeStyleWomen: retrieveSelectableValues(data.women.data)[0],
      };
    case 'dressSize':
    case 'pantsJeanSizeMen':
    case 'pantsJeanSizeWomen':
    case 'shirtSize':
    case 'skirtSize':
    case 'jeanWaistMen':
    case 'jeanWaistWomen':
    case 'topSizeWomen':
    case 'topSizeMen':
    case 'shoeSizeWomen':
    case 'shoeSizeMen':
    case 'suitSize':
    case 'braSize':
      return {
        [attribute]: retrieveSizeValue(data),
      };
    case 'pantsFit':
      return {
        pantsFitMen: retrieveSelectableValues(data)[0],
      };
    case 'stylingOccasions':
    case 'jewelryStyle':
    case 'jewelryTone':
    case 'interests': {
      const [main, other] = retrieveSelectableValues(data);

      return {
        [attribute]: main,
        [`${attribute}Other`]: other,
      };
    }
    default:
      return {
        [attribute]: retrieveSelectableValues(data)[0],
      };
  }
};

/**
 * @param {string} path The path to the updated style preference attribute. @example 'lifestyle.profession'.
 * @param {*} state The state of specific style preference attribute from store.
 * @returns {Object} Payload, containing attribute value.
 */
export const retrieveAttributeValuesFromPreferences = (path, state) => {
  const [section, attribute] = path.split('.');
  const convertedSectionPath = getSectionPath(section);
  const updatedValue = getAttributeValue(state, attribute);

  return _set({}, convertedSectionPath, updatedValue);
};

/**
 * @param {ConvertedStylePreferences} state The style preferences store data.
 * @returns {Object} Payload, containing the values of all attributes.
 */
export const retrieveAllValuesFromPreferences = (state) => {
  const payload = {};
  const sections = ['stylePreferences', 'lifestyle', 'fit', 'shoppingHabits'];

  sections.forEach((section) => {
    const attributes = Object.keys(state[section]);

    attributes.forEach((attribute) => {
      const path = `${section}.${attribute}.data`;
      const attributeState = state[section][attribute].data;
      const attributeValues = retrieveAttributeValuesFromPreferences(path, attributeState);

      _merge(payload, attributeValues);
    });
  });

  return payload;
};
